import { Skeleton, Box } from "@mui/material";
import { useState, useContext } from "react";
import DataGridKeywordExact from "src/components/data-grid/activations/data-grid-keyword-exact";
import ModalCsvImport from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-csv-import";
import { CountryBar } from "src/components/country-bar";
import { useCountryData } from "src/components/visibility";
import { VisibilityContext } from "src/components/visibility/components/VisibilityProvider";
import { useGetKeywordsQuery } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useGridState } from "src/components/data-grid/activations/hooks/useGridState";
import { useDateFilters } from "src/components/data-grid/activations/hooks/useDateFilters";
import { useSelectedCountry } from "src/components/data-grid/activations/hooks/useSelectedCountry";

export default function ActivationsKeywordExact() {
    const workspace: any = useCurrentWorkspace();

    const { page, setPage, pageSize, setPageSize, sortModel, setSortModel, filterModel, setFilterModel } = useGridState();

    const [localPatches, setLocalPatches] = useState({});
    const [openCsvImport, setOpenCsvImport] = useState(false);

    const { countriesData, isLoading } = useCountryData();

    const ciContext = useContext(VisibilityContext);

    const { selectedCountry, setSelectedCountry } = useSelectedCountry(countriesData, ciContext?.selectedCountry);

    const { selectedDateRange, setSelectedDateRange, selectedCompareDate, setSelectedCompareDate } = useDateFilters();

    const { data: keywordsExact, isFetching, refetch } = useGetKeywordsQuery(
        {
            client: workspace?.bqTable,
            page: page + 1,
            page_size: pageSize,
            workspace_id: workspace?.id,
            match_type: "EXACT",
            country: selectedCountry,
            sort: sortModel,
            filters: filterModel,
            date: selectedDateRange?.value || "last30d",
            compareDate: selectedCompareDate?.value,
        },
        {
            skip: !workspace || !selectedCountry,
            refetchOnMountOrArgChange: true,
        }
    );

    const isReady = !!workspace && countriesData.length > 0 && selectedCountry !== null;

    return (
        <>
            <Box>
                <CountryBar
                    countries={countriesData}
                    selectedCountry={selectedCountry}
                    onCountryChange={setSelectedCountry}
                    includeAll
                    loading={isLoading}
                    stickyTop={110}
                    backgroundColor="background.paper"
                    topGap={20}
                    contentPaddingX={"24px"}
                />

                <Box
                    px={"24px"}
                    mt={
                        isLoading
                            ? "24px"
                            : countriesData?.length > 1
                                ? "24px"
                                : "0px"
                    }
                >
                    <Box
                        bgcolor="white"
                        sx={{
                            border: "1px solid rgba(145, 158, 171, 0.20)",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                            height: "720px",
                        }}
                    >
                        {!isReady || isFetching ? (
                            <Skeleton
                                variant="rectangular"
                                sx={{
                                    flex: 1,
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "8px",
                                }}
                            />
                        ) : (
                            <DataGridKeywordExact
                                refetch={refetch}
                                data={keywordsExact?.data ?? []}
                                rowCount={keywordsExact?.total ?? 0}
                                page={page}
                                pageSize={pageSize}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                sortModel={sortModel}
                                setSortModel={setSortModel}
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                localPatches={localPatches}
                                setLocalPatches={setLocalPatches}
                                selectedDateRange={selectedDateRange}
                                selectedCountry={selectedCountry}
                                selectedCompareDate={selectedCompareDate}
                                onDateRangeChange={setSelectedDateRange}
                                onCompareDateChange={setSelectedCompareDate}
                                onOpenCsvImport={() =>
                                    setOpenCsvImport(true)
                                }
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            {workspace && selectedCountry && (
                <ModalCsvImport
                    open={openCsvImport}
                    onClose={() => setOpenCsvImport(false)}
                    matchType="EXACT"
                    workspaceId={workspace.id}
                    client={workspace.bqTable}
                    country={selectedCountry}
                    filters={filterModel}
                />
            )}
        </>
    );
}